import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import { getPerfilCatalogo } from "./api"
import { formatCurrency, formatNumber, formatFecha, segmentoConfig } from "./helpers"

const PAGE_SIZE_OPTS = [50, 100, 200]

const thStyle = {
  background: "#f8fafc", borderBottom: "1px solid #e2e8f0",
  padding: "10px 12px", textAlign: "left",
  fontSize: 11, color: "#475569", fontWeight: 700,
  textTransform: "uppercase", letterSpacing: 0.4, position: "sticky", top: 0, zIndex: 1,
}
const tdStyle = {
  padding: "10px 12px", borderBottom: "1px solid #f1f5f9",
  fontSize: 13, color: "#0f172a", verticalAlign: "middle",
}

const SegmentoChip = ({ segmento, color }) => {
  const cfg = segmentoConfig(segmento)
  const c = color || cfg.color
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      padding: "2px 9px", borderRadius: 999,
      background: `${c}1a`, color: c,
      fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
    }} title={cfg.desc}>
      <span>{cfg.emoji}</span>{cfg.label}
    </span>
  )
}

SegmentoChip.propTypes = { segmento: PropTypes.string, color: PropTypes.string }

const RfmMini = ({ scoreR, scoreF, scoreM }) => {
  const item = (label, score) => {
    let color = "#dc2626"
    if (score >= 5) color = "#16a34a"
    else if (score >= 4) color = "#22c55e"
    else if (score >= 3) color = "#eab308"
    else if (score >= 2) color = "#f97316"
    return (
      <span key={label} style={{
        display: "inline-flex", alignItems: "center",
        background: `${color}1a`, color, padding: "1px 6px",
        borderRadius: 4, fontSize: 10, fontWeight: 700, marginRight: 3,
        minWidth: 26, justifyContent: "center",
      }}>
        {label}{score ?? "—"}
      </span>
    )
  }
  return (
    <div style={{ display: "inline-flex" }}>
      {item("R", scoreR)}{item("F", scoreF)}{item("M", scoreM)}
    </div>
  )
}

RfmMini.propTypes = { scoreR: PropTypes.number, scoreF: PropTypes.number, scoreM: PropTypes.number }

const Insight = ({ texto }) => (
  <span style={{
    display: "inline-block", background: "#f1f5f9", color: "#475569",
    padding: "2px 8px", borderRadius: 999, fontSize: 11,
    marginRight: 4, marginBottom: 2,
  }}>{texto}</span>
)
Insight.propTypes = { texto: PropTypes.string }

const TablaCatalogo = ({
  filtros, segmento, orderBy, orderDir,
  onMetadataChange, onAbrirPerfil, refreshKey,
}) => {
  const [pageSize, setPageSize] = useState(100)
  const [page, setPage] = useState(0)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const offset = page * pageSize

  const cargar = useCallback(async () => {
    if (!filtros) return
    setLoading(true)
    setError("")
    try {
      const r = await getPerfilCatalogo({
        ...filtros,
        segmento: segmento || undefined,
        order_by: orderBy,
        order_dir: orderDir,
        limit: pageSize,
        offset,
      })
      const body = r.data
      if (body?.success === false) throw new Error(body.error || body.message || "Error en el servidor")
      setData(body)
      // Bubble up metadata para chips arriba
      if (body?.metadata && typeof onMetadataChange === "function") {
        onMetadataChange(body.metadata)
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || "No se pudo cargar el catálogo.")
      setData(null)
    } finally {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros, segmento, orderBy, orderDir, pageSize, offset, refreshKey])

  useEffect(() => { cargar() }, [cargar])

  // Reset page al cambiar filtros principales (no offset/pageSize)
  useEffect(() => { setPage(0) }, [filtros, segmento, orderBy, orderDir, refreshKey])

  const receptores = data?.receptores || data?.data || []
  const total = data?.metadata?.total_receptores_disponibles ?? data?.metadata?.total ?? receptores.length
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  const rangoTexto = useMemo(() => {
    if (!receptores.length) return "0 — 0 de 0"
    const desde = offset + 1
    const hasta = Math.min(offset + receptores.length, total)
    return `${formatNumber(desde)} — ${formatNumber(hasta)} de ${formatNumber(total)}`
  }, [offset, receptores.length, total])

  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden" }}>
      <div style={{
        padding: "12px 16px", borderBottom: "1px solid #e2e8f0",
        display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ fontSize: 13, color: "#475569", fontWeight: 600 }}>
          <i className="mdi mdi-account-group me-1 text-primary" />
          Catálogo de clientes — {rangoTexto}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: "#64748b" }}>Filas por página:</span>
          <select
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(0) }}
            style={{
              fontSize: 12, padding: "4px 8px", border: "1px solid #cbd5e1",
              borderRadius: 6, background: "#fff",
            }}
          >
            {PAGE_SIZE_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {error && (
        <div style={{
          padding: "12px 16px", background: "#fef2f2", color: "#991b1b",
          borderBottom: "1px solid #fecaca", display: "flex", alignItems: "center", gap: 8,
        }}>
          <i className="mdi mdi-alert-circle" />
          <span style={{ flex: 1 }}>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={cargar}>Reintentar</button>
        </div>
      )}

      {loading && (
        <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
          <Spinner color="primary" />
          <div className="mt-2">Cargando clientes…</div>
        </div>
      )}

      {!loading && !error && receptores.length === 0 && (
        <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
          <i className="mdi mdi-account-search-outline" style={{ fontSize: 48, color: "#cbd5e1" }} />
          <div className="mt-2">No hay clientes con los filtros actuales.</div>
        </div>
      )}

      {!loading && receptores.length > 0 && (
        <div style={{ overflowX: "auto", maxHeight: "70vh" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
            <thead>
              <tr>
                <th style={thStyle}>Cliente</th>
                <th style={thStyle}>Segmento</th>
                <th style={thStyle}>RFM</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Facturas</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Ingreso neto</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Ticket prom.</th>
                <th style={thStyle}>Última compra</th>
                <th style={thStyle}>Insights</th>
                <th style={{ ...thStyle, textAlign: "center", width: 90 }}>Acción</th>
              </tr>
            </thead>
            <tbody>
              {receptores.map((c, idx) => {
                const rfm = c.rfm || {}
                const insights = c.insights || []
                return (
                  <tr key={`${c.nit_receptor}-${idx}`} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 600, color: "#0f172a" }}>{c.nombre || "—"}</div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>
                        NIT {c.nit_receptor || "—"}
                        {c.nombre_origen && <span style={{ marginLeft: 6, fontSize: 10, color: "#94a3b8" }}>· {c.nombre_origen}</span>}
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <SegmentoChip segmento={rfm.segmento} color={c.color} />
                      {rfm.score_combinado && (
                        <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
                          {rfm.score_combinado}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <RfmMini scoreR={rfm.score_r} scoreF={rfm.score_f} scoreM={rfm.score_m} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>
                      {formatNumber(c.facturas)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: "#10b981" }}>
                      {formatCurrency(c.ingreso_neto)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>
                      {formatCurrency(c.ticket_promedio)}
                    </td>
                    <td style={tdStyle}>
                      <div>{formatFecha(c.ultima_compra)}</div>
                      {c.dias_sin_comprar != null && (
                        <div style={{
                          fontSize: 10,
                          color: c.dias_sin_comprar <= 7 ? "#16a34a" : c.dias_sin_comprar <= 30 ? "#eab308" : "#dc2626",
                          fontWeight: 700,
                        }}>
                          hace {c.dias_sin_comprar}d
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <div style={{ maxWidth: 280 }}>
                        {insights.slice(0, 3).map((ins, i) => <Insight key={i} texto={ins} />)}
                      </div>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => onAbrirPerfil(c)}
                        title="Ver perfil 360°"
                      >
                        <i className="mdi mdi-account-eye-outline" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginación */}
      {!loading && receptores.length > 0 && (
        <div style={{
          padding: "10px 16px", borderTop: "1px solid #e2e8f0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexWrap: "wrap", gap: 8, fontSize: 13, color: "#475569",
        }}>
          <span>{rangoTexto}</span>
          <div style={{ display: "flex", gap: 4 }}>
            <button type="button" className="btn btn-sm btn-outline-secondary"
              disabled={page === 0} onClick={() => setPage(0)} title="Primera">
              <i className="mdi mdi-chevron-double-left" />
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary"
              disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))} title="Anterior">
              <i className="mdi mdi-chevron-left" />
            </button>
            <span style={{
              padding: "0 10px", display: "inline-flex", alignItems: "center",
              fontWeight: 600,
            }}>
              {page + 1} / {totalPages}
            </span>
            <button type="button" className="btn btn-sm btn-outline-secondary"
              disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} title="Siguiente">
              <i className="mdi mdi-chevron-right" />
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary"
              disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)} title="Última">
              <i className="mdi mdi-chevron-double-right" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

TablaCatalogo.propTypes = {
  filtros: PropTypes.object,
  segmento: PropTypes.string,
  orderBy: PropTypes.string,
  orderDir: PropTypes.string,
  onMetadataChange: PropTypes.func,
  onAbrirPerfil: PropTypes.func.isRequired,
  refreshKey: PropTypes.number,
}

export default TablaCatalogo
